import { useEffect, useRef, useState } from "react";
import SliderWithValue from "./SliderWithValue";
import InfoTooltip from "../components/InfoTooltip";

function slugify(value) {
    return value
        ?.toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function AlignmentIcon({ type }) {
    if (type === "left") {
        return (
            <svg viewBox="0 0 16 16" className="w-5 h-5 fill-current">
                <path d="M11 1H1V3H11V1Z" />
                <path d="M1 5H15V7H1V5Z" />
                <path d="M11 9H1V11H11V9Z" />
                <path d="M15 13H1V15H15V13Z" />
            </svg>
        );
    }

    if (type === "center") {
        return (
            <svg viewBox="0 0 16 16" className="w-5 h-5 fill-current">
                <path d="M13 1H3V3H13V1Z" />
                <path d="M1 5H15V7H1V5Z" />
                <path d="M13 9H3V11H13V9Z" />
                <path d="M15 13H1V15H15V13Z" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 16 16" className="w-5 h-5 fill-current">
            <path d="M15 1H5V3H15V1Z" />
            <path d="M1 5H15V7H1V5Z" />
            <path d="M15 9H5V11H15V9Z" />
            <path d="M15 13H1V15H15V13Z" />
        </svg>
    );
}

export default function TitleOptions({
    title,
    value,
    onChange,
    debounceMs = 400,
    variables,
    descriptionShort,
    descriptionLong,
    noTitleCustomization = false,
}) {
    const [state, setState] = useState(() => ({
        visible: true,
        title: "",
        titleSize: 20,
        titleAlignment: "left",
        ...value,
    }));

    // Guard to prevent update loop on initial render
    const didMountRef = useRef(false);

    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }

        const handle = setTimeout(() => {
            onChange?.(state);
        }, debounceMs);

        return () => clearTimeout(handle);

        // IMPORTANT:
        // `onChange` is intentionally omitted from the dependency list.
        // The parent provides a new function reference on every render,
        // which would retrigger this effect and cause an update loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, debounceMs]);

    const enabled = state.visible;
    const disabledClasses = enabled ? "" : "opacity-50 pointer-events-none";
    const namePrefix = slugify(title) || "title-options";

    const alignments = [
        { value: "left", label: "Left" },
        { value: "center", label: "Center" },
        { value: "right", label: "Right" },
    ];

    return (
        <div className="flex flex-col gap-2 p-2">
            <div className="flex items-start justify-between gap-4 mb-1">
                <div className="flex items-center gap-2 uppercase font-bold text-lg text-grey-darkest">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={event =>
                            setState(s => ({
                                ...s,
                                visible: event.target.checked,
                            }))
                        }
                    />
                    <span>{title}</span>
                </div>

                {(descriptionShort || descriptionLong) && (
                    <div className="flex items-center gap-1 max-w-[33%]">
                        {descriptionShort && (
                            <span
                                className="
                                    text-xs text-gray-600 text-right
                                    overflow-hidden
                                    [display:-webkit-box]
                                    [-webkit-line-clamp:2]
                                    [-webkit-box-orient:vertical]
                                "
                            >
                                {descriptionShort}
                            </span>
                        )}
                        {descriptionLong && <InfoTooltip content={descriptionLong} />}
                    </div>
                )}
            </div>

            <div
                className={`flex flex-col gap-2 rounded bg-gray-50 border border-gray-300 shadow-inner shadow-sm p-2 ${disabledClasses}`}
            >
                <input
                    type="text"
                    className="border py-2 px-3 w-full text-grey-darkest"
                    value={state.title}
                    onChange={event =>
                        setState(s => ({
                            ...s,
                            title: event.target.value,
                        }))
                    }
                />

                <div className="flex items-start gap-6 justify-start">
                    {!noTitleCustomization && (
                        <>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs text-gray-500">Font size</span>
                                <SliderWithValue
                                    label={null}
                                    min={15}
                                    max={30}
                                    step={1}
                                    initialValue={state.titleSize}
                                    cooldownMs={debounceMs}
                                    onChangeCommitted={value =>
                                        setState(s => ({
                                            ...s,
                                            titleSize: value,
                                        }))
                                    }
                                />
                            </div>

                            <div className="flex flex-col gap-1">
                                <span className="text-xs text-gray-500">Alignment</span>
                                <div className="flex items-center gap-1">
                                    {alignments.map(a => {
                                        const checked =
                                            state.titleAlignment === a.value;

                                        return (
                                            <label
                                                key={a.value}
                                                title={a.label}
                                                className={`
                                                    cursor-pointer p-1 rounded
                                                    ${
                                                        checked
                                                            ? "bg-gray-200 text-gray-900"
                                                            : "text-gray-500 hover:bg-gray-100"
                                                    }
                                                `}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`${namePrefix}-titleAlignment`}
                                                    checked={checked}
                                                    onChange={() =>
                                                        setState(s => ({
                                                            ...s,
                                                            titleAlignment:
                                                                a.value,
                                                        }))
                                                    }
                                                    className="sr-only"
                                                />
                                                <AlignmentIcon type={a.value} />
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}

                    {Array.isArray(variables) && variables.length > 0 && (
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-gray-500">Variables</span>
                            <div className="flex flex-wrap gap-1 max-h-12 overflow-y-auto overflow-x-hidden pr-1">
                                {[...variables]
                                    .sort((a, b) => a.localeCompare(b))
                                    .map(variable => (
                                        <button
                                            key={variable}
                                            type="button"
                                            className="px-1 py-0.5 text-xs border rounded bg-white hover:bg-gray-100 text-gray-700 leading-tight whitespace-nowrap"
                                            onClick={() =>
                                                setState(s => ({
                                                    ...s,
                                                    title: s.title + variable,
                                                }))
                                            }
                                        >
                                            {variable}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}